import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RaceData } from '../../components/Modals/RaceModal';
import { useAccessToken } from '../useProfile';

type AttachRaceVariables = {
  surveyId: number;
  raceId: number;
};

const getAdminHeaders = (accessToken: string | undefined) => {
  if (!accessToken) {
    throw new Error('No access token available');
  }

  return {
    Authorization: `Bearer ${accessToken}`,
  };
};

export const useAdminImportWorkouts = () => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/import-workouts`,
        {
          method: 'POST',
          headers: {
            ...getAdminHeaders(accessToken),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to import workouts');
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['racePlanSurveys'] });
      queryClient.invalidateQueries({ queryKey: ['allRacePlans'] });
    },
  });
};

export const useAdminCreatePlan = () => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/create-plan`,
        {
          method: 'POST',
          headers: {
            ...getAdminHeaders(accessToken),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create race plan');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['racePlanSurveys'] });
      queryClient.invalidateQueries({ queryKey: ['allRacePlans'] });
    },
  });
};

export const useAdminUpsertRace = () => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (race: RaceData) => {
      const formData = new FormData();

      if (race.id) {
        formData.append('id', race.id.toString());
      }

      formData.append('name', race.name);
      formData.append('date', race.date);
      formData.append('distance', race.distance);
      formData.append('url', race.url);
      formData.append('description', race.description || '');
      formData.append('gpx_file_url', race.gpx_file_url || '');

      if (race.gpx_file) {
        formData.append('gpx', race.gpx_file);
      }

      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races`,
        {
          method: 'POST',
          headers: getAdminHeaders(accessToken),
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error('Failed to save race');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['races'] });
    },
  });
};

export const useAdminAttachRace = () => {
  const { data: accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ surveyId, raceId }: AttachRaceVariables) => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/surveys/${surveyId}/attach-race`,
        {
          method: 'POST',
          headers: {
            ...getAdminHeaders(accessToken),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ raceId }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to attach race to survey');
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['racePlanSurveys'] });
      queryClient.refetchQueries({ queryKey: ['allRacePlans'] });
    },
  });
};
